import { AlertTriangle, Snowflake } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { useWalletStatusActions } from "../hooks/useWalletStatusActions";
import type { RuntimeWallet } from "../types/payments-wallets.types";
import { WalletTargetSummary } from "./WalletTargetSummary";

interface FormValues {
  reason: string;
}

export interface FreezeWalletModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * The wallet being frozen. Taken whole rather than by id so the dialog can name its target: on the wallets
   * home the action is fired from one card out of a grid of identical-looking ones, and a confirmation that
   * does not say _which_ wallet is one mis-click away from freezing the wrong client's money.
   */
  wallet: RuntimeWallet;
  /** Called after a successful freeze so the caller can refetch the wallet. */
  onFrozen: () => void;
}

/** Freeze a wallet with a required reason (INTERNAL staff, `payments:write`). Handoff §3.6. */
export function FreezeWalletModal({ open, onClose, wallet, onFrozen }: FreezeWalletModalProps) {
  const { t } = useTranslations("wallets");
  const { t: tGlobal } = useTranslations("global");
  const { freeze, isSubmitting } = useWalletStatusActions();
  // Backend caps `reason` at 120 chars (FreezeWalletDto).
  const schema = useMemo(
    () => z.object({ reason: z.string().trim().min(1, tGlobal("input_error_required")).max(120) }),
    [tGlobal],
  );
  const form = useZodForm(schema, { reason: "" });

  const onSubmit = async (values: FormValues) => {
    try {
      await freeze(wallet.id, values.reason.trim());
      colocaToast.success(t("statement_freeze_success"));
      form.reset({ reason: "" });
      onFrozen();
      onClose();
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("statement_freeze_error"));
    }
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) {
          form.reset({ reason: "" });
          onClose();
        }
      }}
      title={t("statement_freeze_title_wallet", { currency: wallet.currency })}
      description={t("statement_freeze_intro")}
      // The only ways out are an explicit Cancel or Freeze — no stray × on a delicate action.
      showCloseButton={false}
    >
      <ColocaForm form={form} onSubmit={onSubmit} className="space-y-4">
        {/* What is about to change: the exact wallet, its organization and the balance it holds. */}
        <WalletTargetSummary wallet={wallet} />

        <ColocaAlert variant="warning" icon={AlertTriangle} text={t("statement_freeze_desc")} />

        <ColocaInputField
          name="reason"
          label={t("statement_freeze_reason_label")}
          placeholder={t("statement_freeze_reason_placeholder")}
          maxLength={120}
        />
        <div className="flex justify-end gap-2 pt-2">
          <ColocaButton type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t("statement_freeze_cancel")}
          </ColocaButton>
          <ColocaButton type="submit" variant="destructive" icon={Snowflake} loading={isSubmitting}>
            {t("statement_freeze_confirm")}
          </ColocaButton>
        </div>
      </ColocaForm>
    </ColocaDialog>
  );
}
